import { useNavigate, useLocation, Outlet } from 'react-router';
import { Sidebar, Topbar, type NavGroup } from '@/components';
import { useAuthStore } from '@/stores/auth';
import { useTrialStore } from '@/stores/trial';
import { useApi } from '@/api/hooks';
import styles from './TenantLayout.module.css';

interface TenantConfig {
  company_name: string;
  slug: string;
  settings?: {
    contract?: {
      autoContractedOnDealCompleted?: boolean;
    };
  };
}

const navGroups: NavGroup[] = [
  { label: 'メイン', items: [{ id: 'dashboard', label: 'ダッシュボード', icon: '📊' }] },
  { label: '業務', items: [
    { id: 'properties', label: '物件管理', icon: '🏠' },
    { id: 'crm', label: '顧客・CRM', icon: '👤' },
    { id: 'contracts', label: '契約・書類', icon: '📄' },
    { id: 'portals', label: 'ポータル入稿', icon: '📤' },
    { id: 'reports', label: 'レポート', icon: '📉' },
    { id: 'analytics', label: 'AI動向分析', icon: '🔮' },
  ]},
  { label: '民泊', items: [
    { id: 'minpaku/listings', label: '民泊物件', icon: '🏨' },
    { id: 'minpaku/calendar', label: '予約カレンダー', icon: '📅' },
    { id: 'minpaku/cleaning', label: '清掃管理', icon: '🧹' },
    { id: 'minpaku/revenue', label: 'OTA収支', icon: '💹' },
    { id: 'minpaku/compliance', label: 'コンプライアンス', icon: '⚖️' },
  ]},
  { label: '請求', items: [
    { id: 'billing', label: '請求・入金状況', icon: '💳' },
  ]},
  { label: '設定', items: [
    { id: 'settings', label: 'テナント設定', icon: '⚙️' },
    { id: 'staff', label: 'スタッフ管理', icon: '👥' },
    { id: 'profile', label: 'プロフィール設定', icon: '👤' },
  ]},
];

const pageTitles: Record<string, string> = {
  dashboard: 'ダッシュボード', properties: '物件管理', crm: '顧客・CRM',
  contracts: '契約・書類', portals: 'ポータル入稿', reports: 'レポート', analytics: 'AI動向分析', billing: '請求・入金状況', settings: 'テナント設定', staff: 'スタッフ管理', profile: 'プロフィール設定',
  'minpaku/listings': '民泊物件一覧', 'minpaku/calendar': '予約カレンダー',
  'minpaku/cleaning': '清掃管理', 'minpaku/revenue': 'OTA収支', 'minpaku/compliance': '法的コンプライアンス',
};

function TrialBanner() {
  const { daysLeft, isSuspended } = useTrialStore();
  if (isSuspended) return null; // 期限切れ画面で表示
  if (daysLeft === null) return null;
  const color = daysLeft <= 3 ? '#e53935' : daysLeft <= 7 ? '#f59e0b' : '#0ea5e9';
  const bg = daysLeft <= 3 ? '#fde8e8' : daysLeft <= 7 ? '#fff8e1' : '#e0f2fe';
  return (
    <div style={{ background: bg, color, padding: '8px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center', borderBottom: `2px solid ${color}` }} data-print-hide>
      {daysLeft === 0
        ? 'トライアル期間が本日終了します。有料プランへのアップグレードをご検討ください。'
        : `トライアル残り${daysLeft}日 — 期間終了後はアカウントが一時停止されます。`}
    </div>
  );
}

export function TenantShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { daysLeft, isSuspended } = useTrialStore();
  const { data: config } = useApi<TenantConfig>('/tenant/config');

  // basename適用済みのpathname（/dashboard, /minpaku/listings など）からactiveIdを取得
  const activeId = location.pathname.replace(/^\//, '') || 'dashboard';

  const handleNavigate = (id: string) => navigate(`/${id}`);
  const handleLogout = () => { logout(); navigate('/login'); };

  // トライアル期限切れ画面
  if (isSuspended) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f9f9f7', padding: 20 }}>
        <div style={{ maxWidth: 480, textAlign: 'center', background: '#fff', borderRadius: 16, padding: '48px 32px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>トライアル期間が終了しました</h1>
          <p style={{ fontSize: 14, color: '#6b6960', lineHeight: 1.7, marginBottom: 24 }}>
            ご利用いただきありがとうございます。トライアル期間が終了したため、アカウントが一時停止されています。<br />
            データは保持されていますので、有料プランにアップグレードすると引き続きご利用いただけます。
          </p>
          <a
            href="https://jiayin.jp/#pricing"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', background: '#0ea5e9', color: '#fff', padding: '12px 28px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}
          >
            有料プランを確認する
          </a>
          <div style={{ marginTop: 16 }}>
            <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: '#9e9b91', cursor: 'pointer', fontSize: 13 }}>
              ログアウト
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ロール別ナビゲーション: tenant_staff は「テナント設定」「スタッフ管理」を非表示
  const canManageStaff = user?.role === 'tenant_owner' || user?.role === 'tenant_manager';
  const hiddenForStaff = new Set(['staff', 'settings']);
  const filteredNavGroups = canManageStaff
    ? navGroups
    : navGroups.map(g => ({ ...g, items: g.items.filter(i => !hiddenForStaff.has(i.id)) }));

  return (
    <div className={styles.app} data-app-shell>
      <div data-print-hide>
        <Sidebar
          groups={filteredNavGroups}
          activeId={activeId}
          onNavigate={handleNavigate}
          brand={{ companyName: config?.company_name ?? user?.companyName ?? undefined, slug: config?.slug ?? user?.tenantSlug }}
          user={{ name: user?.name, role: user?.role }}
          onLogout={handleLogout}
        />
      </div>
      <div className={styles.main}>
        <TrialBanner />
        <div data-print-hide><Topbar title={pageTitles[activeId] ?? ''} /></div>
        <div className={styles.content}><Outlet /></div>
      </div>
    </div>
  );
}
